import React, { useMemo, useState, useEffect } from "react";
import { Calculator, Info, Share2, Copy, Check, Image as ImageIcon, Facebook } from "lucide-react";
import { toast } from "sonner";
import { DEPRECIATION_ITEMS, DEPRECIATION_DISCLAIMER, DEPRECIATION_LIABILITY } from "@/data/depreciationDefaults";

/**
 * DeductionFairnessCheck
 *
 * Generalised "Is this deduction fair?" calculator. Renter picks an item
 * from a dropdown, enters its age + original cost + the amount the agent
 * is claiming, and the tool surfaces a depreciated-value estimate so they
 * can see whether the claim is in a reasonable ballpark.
 *
 * Important framings (per product spec):
 *   - This is a GUIDE, not a ruling. Copy uses "may be higher than expected",
 *     "worth checking", never "the agent is wrong".
 *   - Useful-life values are conventions, not statute, and ARE EDITABLE.
 *   - Every result block carries the standard disclaimer.
 *
 * Growth feature: a shareable result card with NO personal details, just
 * the item + numbers + assumptions, plus a watermark. The user can copy a
 * tidy plaintext summary for Facebook captions and screenshot the card
 * itself for the image. A Facebook share-intent link points at the public
 * /check-a-deduction landing page so the click converts into a top-of-
 * funnel visit.
 *
 * @param {string} shareUrl - URL Facebook should share. Defaults to the
 *   public /check-a-deduction landing page on the current origin so even
 *   when embedded in the authed Move-Out workspace, shared links go to a
 *   public, signup-friendly surface.
 */
export default function DeductionFairnessCheck({ shareUrl } = {}) {
  const [itemKey, setItemKey] = useState(DEPRECIATION_ITEMS[0].key);
  const [usefulLife, setUsefulLife] = useState(DEPRECIATION_ITEMS[0].default_useful_life_years);
  const [age, setAge] = useState("");
  const [originalCost, setOriginalCost] = useState("");
  const [claimed, setClaimed] = useState("");
  const [copied, setCopied] = useState(false);

  // When the user changes item, pull in that item's default useful life
  // but keep any value the user has explicitly typed by tracking the
  // last-known default.
  useEffect(() => {
    const it = DEPRECIATION_ITEMS.find((i) => i.key === itemKey);
    if (it) setUsefulLife(it.default_useful_life_years);
  }, [itemKey]);

  const item = DEPRECIATION_ITEMS.find((i) => i.key === itemKey) || DEPRECIATION_ITEMS[0];

  const result = useMemo(() => {
    const a = parseFloat(age);
    const cost = parseFloat(originalCost);
    const claim = parseFloat(claimed);
    const life = parseFloat(usefulLife);
    if (!isFinite(a) || a < 0 || !isFinite(life) || life <= 0) return null;
    const remainingFraction = Math.max(0, 1 - a / life);
    const depreciated = isFinite(cost) && cost > 0 ? cost * remainingFraction : null;
    const gap = depreciated !== null && isFinite(claim) ? claim - depreciated : null;
    return {
      age: a,
      lifeYears: life,
      remainingFraction,
      depreciated,
      claim: isFinite(claim) ? claim : null,
      cost: isFinite(cost) ? cost : null,
      gap,
      pastUsefulLife: a >= life,
    };
  }, [age, originalCost, claimed, usefulLife]);

  const fmt = (n) => (typeof n === "number" && isFinite(n)) ? `$${Math.round(n).toLocaleString()}` : "";

  const summaryText = useMemo(() => {
    if (!result) return "";
    const lines = [
      `TenantTrail bond deduction check`,
      `Item: ${item.label}`,
      `Age: ${result.age} years of an estimated ${result.lifeYears} year useful life`,
    ];
    if (result.cost !== null) lines.push(`Original cost: ${fmt(result.cost)}`);
    if (result.depreciated !== null) lines.push(`Fair depreciated value (estimate): ${fmt(result.depreciated)}`);
    if (result.claim !== null) lines.push(`Agent is claiming: ${fmt(result.claim)}`);
    if (result.gap !== null && result.gap > 0) lines.push(`Difference: ${fmt(result.gap)} higher than the estimate, worth checking.`);
    lines.push("");
    lines.push("General information, not legal advice. Useful-life figures are estimates, not law.");
    lines.push("Calculate your own at tenanttrail.net");
    return lines.join("\n");
  }, [result, item]);

  const copySummary = async () => {
    if (!summaryText) return;
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      toast.success("Summary copied. Paste it into your post.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy automatically. Long-press the card to screenshot instead.");
    }
  };

  return (
    <div className="card" data-testid="deduction-checker">
      <div className="flex items-center gap-2 mb-1">
        <Calculator size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
        <h3 className="font-serif text-lg text-[#0D2B52]">Is this deduction fair?</h3>
      </div>
      <p className="text-[12.5px] text-[#6B7280] mb-4 font-serif leading-snug">
        Tribunals across Australia generally apply a useful-life estimate to each item and reduce a claim by how old the item already is. Pick the item, enter what you know, see whether the agent&apos;s claim is in a reasonable ballpark.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label-mono block mb-1">Item</label>
          <select
            className="input-field"
            value={itemKey}
            onChange={(e) => setItemKey(e.target.value)}
            data-testid="deduction-item-select"
          >
            {DEPRECIATION_ITEMS.map((i) => (
              <option key={i.key} value={i.key}>{i.label}{!i.verified && i.key !== "other" ? " (estimate)" : ""}</option>
            ))}
          </select>
          {item?.notAtoFigure && (
            <div className="font-mono text-[10px] text-[#B45309] mt-1 italic" data-testid="deduction-not-ato">
              Convention only. Paint is not an ATO depreciating asset, so there is no official effective life.
            </div>
          )}
        </div>
        <div>
          <label className="label-mono block mb-1">Common useful-life estimate (years)</label>
          <input
            type="number"
            min="1"
            max="50"
            inputMode="numeric"
            value={usefulLife}
            onChange={(e) => setUsefulLife(e.target.value)}
            className="input-field font-mono"
            data-testid="deduction-useful-life"
          />
          <div className="font-mono text-[10px] text-[#9CA3AF] mt-1 italic">Common estimate, not fixed law. Edit if you have a better one.</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="label-mono block mb-1">Item age (years)</label>
          <input
            type="number"
            min="0"
            max="50"
            inputMode="decimal"
            placeholder="e.g. 8"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            className="input-field font-mono"
            data-testid="deduction-age"
          />
        </div>
        <div>
          <label className="label-mono block mb-1">Original or replacement cost ($)</label>
          <input
            type="number"
            min="0"
            inputMode="decimal"
            placeholder="e.g. 2500"
            value={originalCost}
            onChange={(e) => setOriginalCost(e.target.value)}
            className="input-field font-mono"
            data-testid="deduction-cost"
          />
        </div>
        <div>
          <label className="label-mono block mb-1">Agent is claiming ($)</label>
          <input
            type="number"
            min="0"
            inputMode="decimal"
            placeholder="e.g. 1200"
            value={claimed}
            onChange={(e) => setClaimed(e.target.value)}
            className="input-field font-mono"
            data-testid="deduction-claimed"
          />
        </div>
      </div>

      {result && (result.depreciated !== null || result.pastUsefulLife) && (
        <>
          {/* The shareable result card. Designed so a renter can screenshot
              the visible block and post it. NO personal details — only the
              item, the numbers, the assumptions, and our watermark. */}
          <div
            className="relative bg-white border border-[#E5E4DE] p-5 mb-3 overflow-hidden"
            data-testid="deduction-share-card"
          >
            {/* Watermark band */}
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
              aria-hidden="true"
            >
              <span
                className="font-mono uppercase text-[#1B6C62]/8 tracking-[0.3em] whitespace-nowrap"
                style={{ transform: "rotate(-18deg)", fontSize: "32px", color: "rgba(27, 108, 98, 0.06)" }}
              >
                TenantTrail · tenanttrail.net
              </span>
            </div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#1B6C62]">
                  Is this deduction fair?
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF]">
                  tenanttrail.net
                </div>
              </div>
              <div className="font-serif text-xl text-[#0D2B52] mb-2 leading-tight">
                {item.label}
              </div>

              {result.pastUsefulLife ? (
                <p className="text-[13.5px] text-[#0D2B52] leading-snug mb-3">
                  Based on a common useful-life estimate of <strong>{result.lifeYears} years</strong>, this item is at or past its useful life. The depreciated value is approximately <strong>$0</strong>. Replacement is generally the landlord&apos;s responsibility at this age.
                </p>
              ) : (
                <p className="text-[13.5px] text-[#0D2B52] leading-snug mb-3">
                  Based on a common useful-life estimate of <strong>{result.lifeYears} years</strong>, an item that is <strong>{result.age} years old</strong> has about <strong>{Math.round(result.remainingFraction * 100)}%</strong> of its value remaining.
                  {result.depreciated !== null && (
                    <> The fair depreciated value of this item is about <strong>{fmt(result.depreciated)}</strong>.</>
                  )}
                  {result.claim !== null && result.gap !== null && result.gap > 0 && (
                    <> A claim of <strong>{fmt(result.claim)}</strong> may be higher than expected. It is worth checking.</>
                  )}
                  {result.claim !== null && result.gap !== null && result.gap <= 0 && (
                    <> A claim of <strong>{fmt(result.claim)}</strong> is at or below the depreciated estimate.</>
                  )}
                </p>
              )}

              {/* Assumptions block — shows the maths */}
              <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-3 mb-2">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-[#6B7280] mb-1.5">How this was worked out</div>
                <ul className="text-[11.5px] text-[#1A1A1A] font-mono leading-relaxed space-y-0.5">
                  <li>Item: {item.label}</li>
                  <li>Common useful-life estimate: {result.lifeYears} years</li>
                  <li>Item age: {result.age} years</li>
                  {result.cost !== null && <li>Original / replacement cost: {fmt(result.cost)}</li>}
                  <li>Value remaining: {Math.round(result.remainingFraction * 100)} percent</li>
                  {result.depreciated !== null && <li>Depreciated value: {fmt(result.depreciated)}</li>}
                  {result.claim !== null && <li>Agent is claiming: {fmt(result.claim)}</li>}
                  {result.gap !== null && result.gap > 0 && (
                    <li>Difference: {fmt(result.gap)} above the estimate</li>
                  )}
                </ul>
              </div>

              <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] flex items-start gap-1.5">
                <Info size={9} strokeWidth={1.75} className="mt-0.5 flex-shrink-0" />
                <span className="normal-case tracking-normal font-serif italic text-[10.5px] text-[#6B7280] leading-snug">
                  {DEPRECIATION_DISCLAIMER}
                </span>
              </div>
            </div>
          </div>

          {/* Share actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={copySummary}
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              data-testid="deduction-copy-summary"
            >
              {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.75} />}
              {copied ? "Copied" : "Copy summary for Facebook"}
            </button>
            <a
              href={(() => {
                const canonical = shareUrl || (typeof window !== "undefined" ? `${window.location.origin}/check-a-deduction` : "https://tenanttrail.net/check-a-deduction");
                // Route Facebook's crawler through the backend share-preview
                // endpoint so it unfurls the deduction-specific OG card,
                // instead of the generic site card on the SPA route.
                const fbTarget = canonical.replace(/\/check-a-deduction(\/?$)/, "/api/share/check-a-deduction");
                return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fbTarget)}`;
              })()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              data-testid="deduction-share-facebook"
              onClick={() => toast.success("Tip: paste your copied summary into the Facebook post.")}
            >
              <Facebook size={12} strokeWidth={1.75} />
              Share on Facebook
            </a>
            <span className="font-mono text-[10px] text-[#6B7280] inline-flex items-center gap-1">
              <ImageIcon size={11} strokeWidth={1.75} /> Long-press the card above to screenshot it
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] ml-auto inline-flex items-center gap-1">
              <Share2 size={10} strokeWidth={1.75} /> No personal details included
            </span>
          </div>
        </>
      )}
      <p className="text-[11px] text-[#6B7280] font-serif italic mt-3 leading-snug">
        {DEPRECIATION_DISCLAIMER}
      </p>
      <p className="text-[11px] text-[#6B7280] font-serif italic mt-2 leading-snug" data-testid="deduction-liability">
        {DEPRECIATION_LIABILITY}
      </p>
    </div>
  );
}
